import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, errMsg } from '../../lib/api';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { toast } from 'sonner';

export const IndiaPackCard = ({ tenantId }) => {
  const qc = useQueryClient();
  const { data: cfg, isError } = useQuery({
    queryKey: ['india-config', tenantId],
    queryFn: () => api.get('/india/config', { params: { tenant_id: tenantId } }).then((r) => r.data),
    enabled: !!tenantId, retry: false,
  });
  const [form, setForm] = useState({ udise_code: '', apaar_enabled: false, upi_vpa: '', upi_payee_name: '' });
  useEffect(() => { if (cfg) setForm({ udise_code: cfg.udise_code, apaar_enabled: cfg.apaar_enabled, upi_vpa: cfg.upi_vpa, upi_payee_name: cfg.upi_payee_name }); }, [cfg]);
  const saveMut = useMutation({
    mutationFn: () => api.post('/india/config', { tenant_id: tenantId, ...form }),
    onSuccess: () => { toast.success('India Pack settings saved'); qc.invalidateQueries({ queryKey: ['india-config'] }); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const exportCsv = async () => {
    try {
      const r = await api.get('/india/udise-export.csv', { params: { tenant_id: tenantId }, responseType: 'blob' });
      const url = window.URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url; a.download = 'udise-plus-export.csv'; a.click();
      window.URL.revokeObjectURL(url);
      toast.success('UDISE+ export downloaded');
    } catch (e) { toast.error(errMsg(e)); }
  };
  if (isError || !cfg) return null;

  return (
    <div className="bg-white border border-slate-200 rounded-md p-5 mt-4" data-testid="india-pack-card">
      <h3 className="font-display font-semibold mb-1">India Pack 🇮🇳</h3>
      <p className="font-mono-label text-[10px] uppercase text-slate-400 mb-3">UDISE+ · APAAR · UPI collections - adapter-based, no hard-coding</p>
      <div className="grid sm:grid-cols-2 gap-2">
        <Input data-testid="india-udise-input" placeholder="UDISE+ school code (11 digits)" value={form.udise_code} onChange={(e) => setForm({ ...form, udise_code: e.target.value })} />
        <label className="flex items-center gap-2 text-sm border border-slate-200 rounded-md px-3">
          <input data-testid="india-apaar-toggle" type="checkbox" checked={form.apaar_enabled} onChange={(e) => setForm({ ...form, apaar_enabled: e.target.checked })} />
          Capture APAAR IDs on student records
        </label>
        <Input data-testid="india-upi-vpa-input" placeholder="School UPI VPA (school@upi)" value={form.upi_vpa} onChange={(e) => setForm({ ...form, upi_vpa: e.target.value })} />
        <Input data-testid="india-upi-name-input" placeholder="UPI payee name" value={form.upi_payee_name} onChange={(e) => setForm({ ...form, upi_payee_name: e.target.value })} />
      </div>
      <div className="flex gap-2 mt-3 flex-wrap">
        <Button data-testid="india-save-button" size="sm" className="bg-slate-900 hover:bg-slate-800" disabled={saveMut.isPending} onClick={() => saveMut.mutate()}>Save India Pack</Button>
        <Button data-testid="india-udise-export-button" size="sm" variant="outline" onClick={exportCsv}>Download UDISE+ export</Button>
      </div>
      <div className="mt-3 space-y-0.5">
        {cfg.adapters && Object.entries(cfg.adapters).map(([k, v]) => (
          <p key={k} className="text-[10px] text-slate-400"><span className="font-mono-label uppercase">{k}</span>: {v}</p>
        ))}
      </div>
      <p className="text-[10px] text-slate-400 mt-2">UPI links let guardians pay via any UPI app - reconcile received payments with "Record installment". Live UDISE+/DigiLocker submission adapters activate when government API credentials are configured.</p>
    </div>
  );
};
